import { FormEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { navigation, processSteps, services, site } from './content/site';
import { TurnstileWidget } from './turnstile';
import { turnstileSiteKey } from './turnstile-config';

export function Logo({ variant = 'dark', className = '' }: { variant?: 'dark' | 'light'; className?: string }) {
  const dimensions = { width: 1448, height: 1086 };
  return (
    <img
      className={`brand-logo brand-logo--${variant} ${className}`.trim()}
      src={`/brand/white-ibis-logo-${variant}.png`}
      alt="White Ibis"
      width={dimensions.width}
      height={dimensions.height}
    />
  );
}

export function Header() {
  const [location] = useLocation();
  const [openAtLocation, setOpenAtLocation] = useState<string | null>(null);
  const open = openAtLocation === location;
  const darkHeader = ['/about', '/work'].includes(location);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenAtLocation(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <header className={`site-header ${darkHeader ? 'site-header--dark' : ''}`}>
      <div className="shell header-inner">
        <Link className="header-brand" href="/" aria-label="White Ibis home">
          <Logo variant={darkHeader ? 'light' : 'dark'} />
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navigation.map((item) => (
            <Link className={location === item.href ? 'active' : ''} href={item.href} key={item.href} aria-current={location === item.href ? 'page' : undefined}>
              {item.label}
            </Link>
          ))}
        </nav>
        <Link className="button button--small header-cta" href="/contact?path=audit">
          Start with Troy
        </Link>
        <button
          className="menu-toggle"
          type="button"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          aria-label={open ? 'Close navigation' : 'Open navigation'}
          onClick={() => setOpenAtLocation(open ? null : location)}
        >
          <span />
          <span />
        </button>
      </div>
      <nav id="mobile-navigation" className={`mobile-nav ${open ? 'is-open' : ''}`} aria-label="Mobile navigation">
        <div className="shell">
          {navigation.map((item, index) => (
            <Link href={item.href} key={item.href} onClick={() => setOpenAtLocation(null)}>
              <span>0{index + 1}</span>
              {item.label}
            </Link>
          ))}
          <Link href="/contact?path=audit" onClick={() => setOpenAtLocation(null)}>Start with Troy</Link>
          <Link href="/contact?path=project" onClick={() => setOpenAtLocation(null)}>Discuss a project</Link>
        </div>
      </nav>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="shell footer-brand-row">
        <Link className="footer-brand-link" href="/" aria-label="White Ibis home"><Logo variant="light" className="footer-logo" /></Link>
        <div><p className="eyebrow eyebrow--light">Independent by design</p><h2>Good people.<br />Thoughtful work.</h2></div>
        <Link className="footer-invitation" href="/contact"><span>Have something in mind?</span><strong>Let’s talk <Arrow /></strong></Link>
      </div>
      <div className="shell footer-grid">
        <div className="footer-intro"><p>A personal digital partner for the businesses that make our communities their own.</p><div className="footer-founder"><img src="/images/troy-chiappone.jpeg" width="44" height="44" alt="" loading="lazy" /><span>With Troy Chiappone<br /><small>Founder · Florida</small></span></div></div>
        <nav aria-label="Footer services"><p className="footer-heading">How I can help</p>{services.map((service) => <Link href={`/services#${service.slug}`} key={service.slug}>{service.title}</Link>)}</nav>
        <nav aria-label="Footer company"><p className="footer-heading">Get to know White Ibis</p>{navigation.slice(1).map((item) => <Link href={item.href} key={item.href}>{item.label}</Link>)}<Link href="/contact">Contact</Link></nav>
        <div className="footer-availability"><span className="status-dot" /><p>{site.availability}</p><span>Personal attention.<br />From start to finish.</span></div>
      </div>
      <div className="shell footer-bottom"><span>© {new Date().getFullYear()} White Ibis</span><span>Digital presence. Personal attention.</span><div><Link href="/privacy">Privacy</Link><Link href="/terms">Terms</Link></div></div>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
    </>
  );
}

export function PageMeta({ title, description, path }: { title: string; description: string; path: string }) {
  useEffect(() => {
    document.title = title;
    const canonical = `${site.url}${path === '/' ? '/' : path}`;
    const values: Record<string, string> = {
      'meta[name="description"]': description,
      'meta[property="og:title"]': title,
      'meta[property="og:description"]': description,
      'meta[property="og:url"]': canonical,
    };
    Object.entries(values).forEach(([selector, value]) => document.querySelector(selector)?.setAttribute('content', value));
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
  }, [description, path, title]);
  return null;
}

export function Arrow({ direction = 'right' }: { direction?: 'right' | 'down' }) {
  return (
    <svg className={`arrow arrow--${direction}`} width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M2 9h13M10 4l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function SectionHeading({ label, title, intro, light = false }: { label: string; title: ReactNode; intro?: string; light?: boolean }) {
  return (
    <div className={`section-heading ${light ? 'section-heading--light' : ''}`}>
      <p className={`eyebrow ${light ? 'eyebrow--light' : ''}`}>{label}</p>
      <div>
        <h2>{title}</h2>
        {intro && <p>{intro}</p>}
      </div>
    </div>
  );
}

export function ButtonRow() {
  return (
    <div className="button-row">
      <Link className="button" href="/contact?path=audit">Start with Troy <Arrow /></Link>
      <Link className="text-link" href="/process">See how we’ll work <Arrow /></Link>
    </div>
  );
}

export function ClosingCta() {
  return (
    <section className="closing-cta">
      <div className="shell closing-grid">
        <p className="eyebrow">Your next chapter</p>
        <div>
          <h2>Let’s make your business<br /><em>look as good as it is.</em></h2>
          <p>Tell me what you do, what feels out of date, and where you’d like to go. We’ll start with a conversation.</p>
          <ButtonRow />
        </div>
      </div>
    </section>
  );
}

export function OwnershipMap() {
  const nodes = ['Domain', 'Website', 'Store', 'Customer list', 'Analytics', 'Payments'];
  return (
    <div className="ownership-map" role="img" aria-label="Diagram showing a company at the center of its domain, website, store, customer list, analytics, and payments">
      <div className="map-center"><span>Your company</span><small>Primary owner</small></div>
      {nodes.map((node, index) => <div className={`map-node map-node--${index + 1}`} key={node}>{node}</div>)}
      <svg viewBox="0 0 600 430" aria-hidden="true">
        <path d="M300 215L130 85M300 215L300 55M300 215L470 85M300 215L130 345M300 215L300 375M300 215L470 345" />
      </svg>
    </div>
  );
}

export function ServiceIcon({ number }: { number: string }) {
  const paths: Record<string, ReactNode> = {
    '01': <><path d="M29 14c0 8-11 18-11 18S7 22 7 14a11 11 0 1 1 22 0Z" /><circle cx="18" cy="14" r="4" /></>,
    '02': <><rect x="3" y="5" width="30" height="25" rx="2" /><path d="M3 12h30M8 9h1m3 0h1m3 0h1M8 18h9m-9 5h15" /></>,
    '03': <><path d="m5 15 22-9v23L5 22v-7ZM27 13l5-2m-5 13 5 2M9 23l2 8h6l-3-6M5 16H2v5h3" /></>,
    '04': <><rect x="3" y="3" width="12" height="9" rx="2" /><rect x="21" y="24" width="12" height="9" rx="2" /><path d="M9 12v12a4 4 0 0 0 4 4h8m-4-4 4 4-4 4M15 7h8a4 4 0 0 1 4 4v7m-4-4 4 4 4-4" /></>,
    '05': <><rect x="3" y="7" width="30" height="23" rx="2" /><path d="m3 9 15 12L33 9M3 28l10-11m20 11L23 17" /></>,
    '06': <><rect x="3" y="8" width="30" height="22" rx="3" /><path d="M3 15h30M9 23h7M24 3v8m-4-4h8" /></>,
    '07': <><path d="m7 24 3-9L24 1l7 7-14 14-10 2Z M20 5l7 7M7 24l7-2-4-7" /><path d="M4 31h28" /></>,
    '08': <><rect x="3" y="8" width="30" height="24" rx="2" /><path d="M3 15h30M8 8l5 7m4-7 5 7m4-7 5 7m-18 5v8l7-4-7-4Z" /></>,
  };
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[number]}</svg>;
}

export function ServiceRail({ limit }: { limit?: number }) {
  return (
    <div className="service-rail">
      {services.slice(0, limit).map((service) => (
        <Link href={`/services#${service.slug}`} className="service-row" key={service.slug}>
          <div className="service-card-top"><ServiceIcon number={service.number} /><span>{service.number}</span></div>
          <h3>{service.title}</h3><p>{service.short}</p><span className="service-card-link">Explore service <Arrow /></span>
        </Link>
      ))}
    </div>
  );
}

export function ProcessStrip() {
  return (
    <ol className="process-strip">
      {processSteps.map((step) => (
        <li key={step.title}>
          <span>{step.number}</span>
          <h3>{step.title}</h3>
          <p>{step.summary}</p>
        </li>
      ))}
    </ol>
  );
}

type IntakeState = 'idle' | 'submitting' | 'success' | 'error' | 'verification-error';

export function IntakeForm() {
  const [state, setState] = useState<IntakeState>('idle');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const formId = useId();
  const params = new URLSearchParams(window.location.search);
  const requestedCare = params.get('service') === 'ongoing-stewardship';
  const requestedService = requestedCare ? 'Several services' : services.find((service) => service.slug === params.get('service'))?.title || '';
  const requestedPath = requestedCare ? 'Ongoing stewardship' : params.get('path') === 'audit' ? 'Digital audit' : params.get('path') === 'project' ? 'Defined project' : '';

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    if (!turnstileToken) {
      setState('verification-error');
      setMessage('Please complete the security check before sending your project details.');
      return;
    }
    setState('submitting');
    setMessage('');

    try {
      const body = new FormData(form);
      body.set('cf-turnstile-response', turnstileToken);
      const response = await fetch('/api/contact', {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      });
      const result = await response.json().catch(() => null) as { ok?: boolean; code?: string } | null;
      if (!response.ok || result?.ok !== true) {
        setState(result?.code?.startsWith('verification-') ? 'verification-error' : 'error');
        setTurnstileToken('');
        setTurnstileReset((value) => value + 1);
        setMessage(
          result?.code === 'verification-expired'
            ? 'The security check expired. Please complete it again and submit your project details.'
            : result?.code?.startsWith('verification-')
              ? 'The security check was not accepted. Please complete it again.'
              : 'Your message was not sent. Please try again shortly.',
        );
        return;
      }
      form.reset();
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
      setState('success');
      setMessage('Thank you. Your project details have been received, and White Ibis will reply by email.');
    } catch {
      setTurnstileToken('');
      setTurnstileReset((value) => value + 1);
      setState('error');
      setMessage('Your message was not sent. Please try again shortly.');
    }
  }

  return (
    <form ref={formRef} className="intake-form" onSubmit={submit} aria-describedby={`${formId}-privacy ${formId}-status`}>
      <input type="hidden" name="_subject" value="White Ibis project inquiry" />
      <input className="honeypot" type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <fieldset className="form-section"><legend><span>01</span> You & your business</legend><div className="form-grid">
        <Field label="Your name" name="name" required autoComplete="name" />
        <Field label="Business name" name="business" required autoComplete="organization" />
        <Field label="Email" name="email" type="email" required autoComplete="email" inputMode="email" />
        <Field label="Phone (optional)" name="phone" type="tel" autoComplete="tel" inputMode="tel" />
      </div></fieldset>
      <fieldset className="form-section"><legend><span>02</span> What you have in mind</legend><div className="form-grid">
        <Field label="Current website (optional)" name="website" type="url" inputMode="url" placeholder="https://" />
        <label>
          <span>How can I help?<span className="required-mark" aria-hidden="true"> *</span></span>
          <select name="engagement" required defaultValue={requestedPath}>
            <option value="" disabled>Select one</option>
            <option>Digital audit</option>
            <option>Defined project</option>
            <option>Ongoing stewardship</option>
            <option>Not sure yet</option>
          </select>
        </label>
        <label>
          <span>Services of interest<span className="required-mark" aria-hidden="true"> *</span></span>
          <select name="services" required defaultValue={requestedService}>
            <option value="" disabled>Select one</option>
            {services.map((service) => <option key={service.title}>{service.title}</option>)}
            <option>Several services</option>
          </select>
        </label>
        <label>
          <span>Approximate timeline<span className="required-mark" aria-hidden="true"> *</span></span>
          <select name="timeline" required defaultValue="">
            <option value="" disabled>Select one</option>
            <option>As soon as practical</option>
            <option>Within 1–3 months</option>
            <option>Within 3–6 months</option>
            <option>Planning for later</option>
          </select>
        </label>
        <label>
          <span>Approximate project budget<span className="required-mark" aria-hidden="true"> *</span></span>
          <select name="budget" required defaultValue="">
            <option value="" disabled>Select one</option>
            <option>Under $5,000</option>
            <option>$5,000–$10,000</option>
            <option>$10,000–$25,000</option>
            <option>$25,000+</option>
            <option>Need help defining scope</option>
          </select>
        </label>
        <label className="form-wide">
          <span>Primary business challenge<span className="required-mark" aria-hidden="true"> *</span></span>
          <textarea name="challenge" rows={4} required placeholder="What is difficult, unclear, outdated, or getting in the way?" />
        </label>
      </div></fieldset>
      <fieldset className="form-section"><legend><span>03</span> A few helpful details</legend><div className="form-grid">
        <Field label="Current platform (optional)" name="platform" placeholder="Shopify, WordPress, Mailchimp…" />
        <Field label="Physical locations (optional)" name="locations" inputMode="numeric" placeholder="One, several, or none" />
        <label className="form-wide">
          <span>Anything else I should know? (optional)</span>
          <textarea name="message" rows={4} />
        </label>
      </div></fieldset>
      <label className="consent">
        <input type="checkbox" name="consent" required />
        <span id={`${formId}-privacy`}>I agree that White Ibis may use these details to respond to this inquiry. See the <Link href="/privacy">privacy notice</Link>.<span className="required-mark" aria-hidden="true"> *</span></span>
      </label>
      <TurnstileWidget
        siteKey={turnstileSiteKey}
        resetSignal={turnstileReset}
        onVerify={(token) => {
          setTurnstileToken(token);
          if (state === 'verification-error') {
            setState('idle');
            setMessage('');
          }
        }}
        onExpire={() => {
          setTurnstileToken('');
          setState('verification-error');
          setMessage('The security check expired. Please complete it again.');
        }}
        onError={() => {
          setTurnstileToken('');
          setState('verification-error');
          setMessage('The security check could not load. Please refresh the page and try again.');
        }}
      />
      <p className="form-security-note">Protected by Cloudflare Turnstile. Contact details stay private until a verified inquiry reaches White Ibis.</p>
      <button className="button submit-button" type="submit" disabled={state === 'submitting' || !turnstileToken || !turnstileSiteKey}>
        {state === 'submitting' ? 'Sending…' : 'Send project details'} <Arrow />
      </button>
      <div id={`${formId}-status`} className={`form-status form-status--${state === 'verification-error' ? 'error' : state}`} role="status" aria-live="polite">
        {message}
      </div>
    </form>
  );
}

type FieldProps = {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: 'email' | 'tel' | 'url' | 'numeric';
  placeholder?: string;
};

function Field({ label, name, type = 'text', required, autoComplete, inputMode, placeholder }: FieldProps) {
  return (
    <label>
      <span>{label}{required && <span className="required-mark" aria-hidden="true"> *</span>}</span>
      <input name={name} type={type} required={required} autoComplete={autoComplete} inputMode={inputMode} placeholder={placeholder} />
    </label>
  );
}
